import { useState, useEffect, useCallback } from 'react'
import { getCaselawReferenceHtmlForStandaloneViewerV3 } from '../fetch/getCaselawReferenceHtmlForStandaloneViewerV3'
import { nanoid } from 'nanoid'
import * as Sentry from '@sentry/browser'
import { CaselawReferenceMetadataResponseV3 } from '../schema/caselaw-reference-viewer.schema'

interface UseGetCaselawReferenceHtmlState {
  data: string | null
  isLoading: boolean
  isError: boolean
  refetch: () => void
}

type CaselawReferenceHtmlResult = Omit<UseGetCaselawReferenceHtmlState, 'refetch'> & {
  storageIdentifier: string | null
}

/**
 * Use Get Caselaw Reference HTML
 * Fetch the HTML for a caselaw reference via the V3 endpoint.
 * @param metadata - The V3 metadata of the caselaw reference, or `null`/`undefined`
 *   while the caller is still loading it. When not yet available, the hook waits in
 *   an idle state without fetching or erroring.
 * @returns The HTML for the given metadata
 */
export function useGetCaselawReferenceHtmlInStandalone(metadata: CaselawReferenceMetadataResponseV3 | null | undefined): UseGetCaselawReferenceHtmlState {
  const storageIdentifier = metadata?.s3_path ?? null
  const metadataForDiagnostics = JSON.stringify(metadata)
  const [retryAttempt, setRetryAttempt] = useState(0)
  const [result, setResult] = useState<CaselawReferenceHtmlResult>({
    storageIdentifier,
    data: null,
    isLoading: Boolean(storageIdentifier),
    isError: false,
  })

  useEffect(() => {
    let cancelled = false

    if (!storageIdentifier) {
      setResult({ storageIdentifier: null, data: null, isLoading: false, isError: false })
      return () => {
        cancelled = true
      }
    }

    setResult({ storageIdentifier, data: null, isLoading: true, isError: false })

    const fetchData = async () => {
      try {
        const response = await getCaselawReferenceHtmlForStandaloneViewerV3(storageIdentifier)
        if (cancelled) return
        if (!response) {
          throw new Error(`No HTML found for reference metadata${metadataForDiagnostics}`)
        }

        setResult({ storageIdentifier, data: response.data, isLoading: false, isError: false })
      } catch (error) {
        if (cancelled) return

        const sentry_transaction_id = nanoid()
        Sentry.withScope((scope) => {
          scope.setTags({ transaction_id: sentry_transaction_id })
          Sentry.captureException(new Error(`Reference HTML fetch error - detail: ${error}`), {
            extra: {
              onLine: navigator.onLine,
              cookieEnabled: navigator.cookieEnabled,
              metadata: metadataForDiagnostics,
            },
          })
        })
        setResult({ storageIdentifier, data: null, isLoading: false, isError: true })
      }
    }

    void fetchData()
    return () => {
      cancelled = true
    }
  }, [metadataForDiagnostics, retryAttempt, storageIdentifier])

  const refetch = useCallback(() => {
    setRetryAttempt((attempt) => attempt + 1)
  }, [])

  const isCurrentRequest = result.storageIdentifier === storageIdentifier

  return {
    data: isCurrentRequest ? result.data : null,
    isLoading: isCurrentRequest ? result.isLoading : Boolean(storageIdentifier),
    isError: isCurrentRequest ? result.isError : false,
    refetch,
  }
}
